import React, { useEffect, useState } from 'react';
import confetti from '@/confetti';
import PostcardGenerator from './PostcardGenerator';
import ShareActions from './ShareActions';

interface SessionCompleteProps {
  session: ReturnType<typeof import('@/hooks/useSession').useSession>;
  othersFocusing?: number;
}

const SessionComplete: React.FC<SessionCompleteProps> = ({ session, othersFocusing = 0 }) => {
  const [sessionCount, setSessionCount] = useState(1);
  const [postcardBlob, setPostcardBlob] = useState<Blob | null>(null);

  // Get today's session count and trigger confetti
  useEffect(() => {
    const todayKey = new Date().toLocaleDateString();
    const storedCount = localStorage.getItem(`sessions_${todayKey}`);
    const currentCount = storedCount ? parseInt(storedCount, 10) + 1 : 1;
    localStorage.setItem(`sessions_${todayKey}`, String(currentCount));
    setSessionCount(currentCount);

    // Fire confetti
    confetti.start();
    const timer = setTimeout(() => confetti.stop(), 5000);

    // Play completion sound
    const sound = new Audio('/notify-new.mp3');
    sound.volume = 0.8;
    sound.play().catch(() => {});

    return () => {
      clearTimeout(timer);
      confetti.remove();
    };
  }, []);

  const world = session.context.world;
  const minutes = session.context.duration;

  const shareUrl = `https://kaizenflow.co/enter/${world.id}?d=${minutes}&t=${encodeURIComponent(session.context.task)}`;

  const tasks = session.context.tasks;

  return (
    <div className="th-complete">
      <div className="th-complete-inner">
        <p className="th-overline">session complete</p>
        <h1 className="th-complete-title">
          {minutes} minutes
          <em>well spent.</em>
        </h1>

        {tasks.length > 0 && (
          <ul className="th-complete-tasks">
            {tasks.map((t) => (
              <li key={t.id} className={t.completed ? 'is-done' : ''}>
                {t.text}
              </li>
            ))}
          </ul>
        )}

        <div className="th-postcard">
          <PostcardGenerator
            world={world}
            task={session.context.task}
            duration={minutes}
            onGenerate={setPostcardBlob}
          />
        </div>

        <div className="th-share">
          <ShareActions
            postcardBlob={postcardBlob}
            shareUrl={shareUrl}
            sessionCount={sessionCount}
            worldName={world.name}
          />
        </div>

        <div className="th-complete-actions">
          <button onClick={session.restart} className="th-enter">
            Save session
          </button>
          <button onClick={session.exit} className="th-link">
            Share
          </button>
        </div>

        <p className="th-foot">
          session {sessionCount} today
          {othersFocusing > 0 && ` · ${othersFocusing} still focusing`}
        </p>
      </div>
    </div>
  );
};

export default SessionComplete;
